import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { DomainesDialog } from '../components/DomainesDialog';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { StatusBadge } from '../components/common/StatusBadge';
import { 
  Plus, Search, GraduationCap, Clock, BookOpen, Pencil, Trash2, X, Copy,
  FileText, ChevronUp, ChevronDown, Award, Shield, ArrowLeft, GripVertical, Briefcase,
  Upload, CheckCircle, AlertTriangle, Loader2, MapPin, FolderTree, FileDown, BookOpenText
} from 'lucide-react';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { franceCompetencesUrl } from '../lib/certifications';
import { DecoupageJournees } from '../components/DecoupageJournees';
import { CompetencesProEditor, CompetencesProList } from '../components/CompetencesPro';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { saveBlob } from '../lib/download';
import { TestsLiesModule } from '../components/formation/TestsLiesModule';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Titres des compétences professionnelles à atteindre (liste structurée ou ancien texte libre)
const competencesTitres = (f) => {
  const list = (f.competences_pro || []).map(c => (c.titre || '').trim()).filter(Boolean);
  if (list.length) return list;
  return (f.competences_professionnelles || '').split('\n').map(l => l.replace(/^[-•*\s]+/, '').trim()).filter(Boolean);
};

const TYPES_FORMATION = [
  { value: 'presentiel', label: 'Présentiel' },
  { value: 'distanciel', label: 'Distanciel' },
  { value: 'mixte', label: 'Mixte' },
  { value: 'elearning', label: 'E-learning' },
  { value: 'afest', label: 'AFEST' }
];

export const FormationsPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [formations, setFormations] = useState([]);
  const [modules, setModules] = useState([]);
  const [domaines, setDomaines] = useState([]);
  const [certifications, setCertifications] = useState([]);
  const [codesNsf, setCodesNsf] = useState([]);
  const [typesFormation, setTypesFormation] = useState(TYPES_FORMATION);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState('code');
  const [sortDirection, setSortDirection] = useState('asc');
  const [filterDomaine, setFilterDomaine] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [filterCertif, setFilterCertif] = useState('all');
  const [filterDuree, setFilterDuree] = useState('all');
  const [filterDureeMin, setFilterDureeMin] = useState('');
  const [filterDureeMax, setFilterDureeMax] = useState('');
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formTab, setFormTab] = useState('general');
  const [deleteId, setDeleteId] = useState(null);
  const [editingFormation, setEditingFormation] = useState(null);
  const [selectedFormation, setSelectedFormation] = useState(null);
  const [downloadingPlaquette, setDownloadingPlaquette] = useState(null);

  const downloadPlaquette = async (formation, detaillee) => {
    setDownloadingPlaquette(detaillee ? 'detaillee' : 'simple');
    try {
      const res = await axios.get(`${API_URL}/formations/${formation.id}/plaquette`, {
        params: { detaillee },
        responseType: 'blob',
      });
      saveBlob(new Blob([res.data], { type: 'application/pdf' }), `Plaquette_${formation.code}_${detaillee ? 'detaillee' : 'simple'}.pdf`);
      toast.success('Plaquette téléchargée');
    } catch (err) {
      toast.error('Erreur lors de la génération de la plaquette');
    } finally {
      setDownloadingPlaquette(null);
    }
  };
  const [formationActions, setFormationActions] = useState([]);
  
  // Dialog for creating new domaine
  const [domaineDialogOpen, setDomaineDialogOpen] = useState(false);
  const [domainesManagerOpen, setDomainesManagerOpen] = useState(false);
  const [newDomaine, setNewDomaine] = useState({ nom: '', abreviation: '' });
  const [creatingDomaine, setCreatingDomaine] = useState(false);
  const [viewingCertification, setViewingCertification] = useState(null);

  // Import modules state
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [importLoading, setImportLoading] = useState(false);
  const [modulesToCreate, setModulesToCreate] = useState([]);
  const [creatingModules, setCreatingModules] = useState(false);

  const emptyForm = {
    code: '',
    titre: '',
    type_formation: 'presentiel',
    domaine_id: '',
    duree_totale_heures: 0,
    objectif_pedagogique: '',
    competences_professionnelles: '',
    competences_pro: [],
    type_certification: 'qualifiante',
    reference_rncp: '',
    certification_id: '',
    code_nsf: '',
    modules_formation: [],
    modules: [],
    heures_par_jour: 7,
    decoupage_journees: null,
    programme: '',
    niveau_acces: '',
    niveau_sortie: '',
    metiers: '',
    plus_pedagogiques: '',
    modalites_organisation: '',
    modalites_evaluation: '',
    tarif_inter: '',
    tarif_intra: '',
    lieu_formation: '',
  };

  const [formData, setFormData] = useState(emptyForm);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [formationsRes, modulesRes, domainesRes, certsRes, nsfRes, typesRes] = await Promise.all([
        axios.get(`${API_URL}/formations`),
        axios.get(`${API_URL}/modules`),
        axios.get(`${API_URL}/domaines`),
        axios.get(`${API_URL}/certifications`),
        axios.get(`${API_URL}/codes-nsf`),
        axios.get(`${API_URL}/types-formation`).catch(() => ({ data: null })),
      ]);
      setFormations([...formationsRes.data].sort((a, b) => (a.code || '').localeCompare(b.code || '')));
      setModules(modulesRes.data);
      setDomaines(domainesRes.data);
      setCertifications(certsRes.data);
      setCodesNsf(nsfRes.data);
      if (typesRes.data && typesRes.data.length) setTypesFormation(typesRes.data);
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  // Handle URL parameter for edit mode (from DossierFormPage)
  useEffect(() => {
    const editId = searchParams.get('edit');
    if (editId && formations.length > 0 && !dialogOpen) {
      const formation = formations.find(f => f.id === editId);
      if (formation) {
        handleOpenDialog(formation);
        setSearchParams({}, { replace: true }); // Clean URL
      }
    }
    const viewId = searchParams.get('view');
    if (viewId && formations.length > 0 && !selectedFormation) {
      const formation = formations.find(f => f.id === viewId);
      if (formation) {
        setSelectedFormation(formation);
        setSearchParams({}, { replace: true });
      }
    }
  }, [searchParams, formations, dialogOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch actions linked to selected formation
  useEffect(() => {
    if (selectedFormation) {
      const fetchActions = async () => {
        try {
          const res = await axios.get(`${API_URL}/dossiers`);
          const linked = res.data.filter(d => d.formation_id === selectedFormation.id);
          setFormationActions(linked);
        } catch { setFormationActions([]); }
      };
      fetchActions();
    } else {
      setFormationActions([]);
    }
  }, [selectedFormation]);


  // Auto-generate code when domaine, titre, or duree change
  const generateCode = useCallback((data) => {
    const domaine = domaines.find(d => d.id === data.domaine_id);
    if (!domaine || !data.titre) return '';
    // Create title abbreviation: take first letters of each word, uppercase, max 6 chars
    const titreAbrev = data.titre
      .split(/[\s\-_]+/)
      .map(w => w.charAt(0).toUpperCase())
      .join('')
      .substring(0, 6);
    return `F${domaine.abreviation}-${titreAbrev}${data.duree_totale_heures || 0}`;
  }, [domaines]);

  const handleClone = async (formation) => {
    try {
      const res = await axios.post(`${API_URL}/formations/${formation.id}/clone`);
      toast.success(`Formation clonée : ${res.data.code}`);
      await fetchData();
      setSelectedFormation(null);
      handleOpenDialog(res.data);
    } catch (error) {
      toast.error('Erreur lors du clonage');
    }
  };

  const handleOpenDialog = (formation = null) => {
    if (formation) {
      setEditingFormation(formation);
      setFormData({
        code: formation.code,
        titre: formation.titre,
        type_formation: formation.type_formation,
        domaine_id: formation.domaine_id || '',
        duree_totale_heures: formation.duree_totale_heures || 0,
        objectif_pedagogique: formation.objectif_pedagogique || '',
        competences_professionnelles: formation.competences_professionnelles || '',
        competences_pro: formation.competences_pro || [],
        type_certification: formation.type_certification || 'qualifiante',
        reference_rncp: formation.reference_rncp || '',
        certification_id: formation.certification_id || '',
        code_nsf: formation.code_nsf || '',
        modules_formation: formation.modules_formation || [],
        modules: formation.modules || [],
        heures_par_jour: formation.heures_par_jour || 7,
        decoupage_journees: formation.decoupage_journees || null,
        programme: formation.programme || '',
        niveau_acces: formation.niveau_acces || '',
        niveau_sortie: formation.niveau_sortie || '',
        metiers: formation.metiers || '',
        plus_pedagogiques: formation.plus_pedagogiques || '',
        modalites_organisation: formation.modalites_organisation || '',
        modalites_evaluation: formation.modalites_evaluation || '',
        tarif_inter: formation.tarif_inter ?? '',
        tarif_intra: formation.tarif_intra ?? '',
        lieu_formation: formation.lieu_formation || '',
      });
    } else {
      setEditingFormation(null);
      setFormData({ ...emptyForm });
    }
    setFormTab('general');
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Sync legacy modules list from modules_formation
      const dataToSend = {
        ...formData,
        modules: formData.modules_formation.map(mf => mf.module_id),
        heures_par_jour: parseFloat(formData.heures_par_jour) || 7,
        decoupage_journees: formData.decoupage_journees
          ? formData.decoupage_journees
              .map(d => ({ segments: d.segments.filter(sg => sg.heures > 0 && formData.modules_formation.some(mf => mf.module_id === sg.module_id)) }))
              .filter((d, i, arr) => d.segments.length > 0 || i === arr.length - 1)
          : null,
        tarif_inter: formData.tarif_inter !== '' ? parseFloat(formData.tarif_inter) : null,
        tarif_intra: formData.tarif_intra !== '' ? parseFloat(formData.tarif_intra) : null,
      };
      if (editingFormation) {
        await axios.put(`${API_URL}/formations/${editingFormation.id}`, dataToSend);
        toast.success('Formation mise à jour');
      } else {
        await axios.post(`${API_URL}/formations`, dataToSend);
        toast.success('Formation créée');
      }
      setDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la sauvegarde');
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API_URL}/formations/${deleteId}`);
      setFormations(formations.filter(f => f.id !== deleteId));
      if (selectedFormation && selectedFormation.id === deleteId) setSelectedFormation(null);
      toast.success('Formation supprimée');
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    } finally {
      setDeleteId(null);
    }
  };

  const handleCreateDomaine = async () => {
    if (!newDomaine.nom || !newDomaine.abreviation) return;
    setCreatingDomaine(true);
    try {
      const res = await axios.post(`${API_URL}/domaines`, newDomaine);
      setDomaines(prev => [...prev, res.data]);
      setFormData(prev => ({ ...prev, domaine_id: res.data.id }));
      setDomaineDialogOpen(false);
      setNewDomaine({ nom: '', abreviation: '' });
      toast.success('Domaine créé');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    } finally {
      setCreatingDomaine(false);
    }
  };

  // Module management in formation
  const addModuleToFormation = (moduleId) => {
    const mod = modules.find(m => m.id === moduleId);
    if (!mod || formData.modules_formation.some(mf => mf.module_id === moduleId)) return;
    const newMf = {
      module_id: moduleId,
      duree_heures: mod.duree_heures || 0,
      ordre: formData.modules_formation.length + 1
    };
    setFormData(prev => ({ ...prev, modules_formation: [...prev.modules_formation, newMf] }));
  };

  const removeModuleFromFormation = (moduleId) => {
    const updated = formData.modules_formation
      .filter(mf => mf.module_id !== moduleId)
      .map((mf, idx) => ({ ...mf, ordre: idx + 1 }));
    setFormData(prev => ({ ...prev, modules_formation: updated }));
  };

  const updateModuleDuree = (moduleId, duree) => {
    setFormData(prev => ({
      ...prev,
      modules_formation: prev.modules_formation.map(mf =>
        mf.module_id === moduleId ? { ...mf, duree_heures: parseInt(duree) || 0 } : mf
      )
    }));
  };

  const moveModule = (moduleId, direction) => {
    const list = [...formData.modules_formation].sort((a, b) => a.ordre - b.ordre);
    const idx = list.findIndex(mf => mf.module_id === moduleId);
    if ((direction === -1 && idx === 0) || (direction === 1 && idx === list.length - 1)) return;
    const swapIdx = idx + direction;
    [list[idx], list[swapIdx]] = [list[swapIdx], list[idx]];
    const reordered = list.map((mf, i) => ({ ...mf, ordre: i + 1 }));
    setFormData(prev => ({ ...prev, modules_formation: reordered }));
  };

  const [moduleFilter, setModuleFilter] = useState('all');
  const [moduleSearch, setModuleSearch] = useState('');

  // ===== IMPORT MODULES FROM EXCEL =====
  const handleImportFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    setImportLoading(true);
    setImportResult(null);
    setModulesToCreate([]);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API_URL}/modules/import-check`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setImportResult(res.data);
      if (res.data.not_found.length > 0) {
        setModulesToCreate(res.data.not_found.map(m => ({ ...m, selected: true })));
      }
      setImportDialogOpen(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de l'import");
    } finally {
      setImportLoading(false);
    }
  };

  const handleCreateMissingModules = async () => {
    const toCreate = modulesToCreate.filter(m => m.selected);
    if (toCreate.length === 0) return;
    setCreatingModules(true);
    try {
      const res = await axios.post(`${API_URL}/modules/batch-create`, {
        modules: toCreate.map(m => ({
          code: m.code, titre: m.titre, duree_heures: m.duree_heures,
          contenu: m.contenu, objectifs: m.objectifs,
        }))
      });
      toast.success(`${res.data.count} module(s) cree(s)`);
      // Refresh modules list
      const modRes = await axios.get(`${API_URL}/modules`);
      setModules(modRes.data);
      // Move created modules from not_found to found
      setImportResult(prev => {
        const newFound = [...prev.found];
        for (const created of res.data.created) {
          const idx = prev.not_found.findIndex(nf => nf.code.toUpperCase() === created.code.toUpperCase());
          if (idx >= 0) {
            newFound.push({
              code: created.code, titre: created.titre,
              duree_heures: created.duree_heures, db_module: created,
            });
          }
        }
        return { ...prev, found: newFound, not_found: [] };
      });
      setModulesToCreate([]);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur creation modules');
    } finally {
      setCreatingModules(false);
    }
  };

  const confirmImportModules = () => {
    if (!importResult) return;
    const allFound = importResult.found;
    if (allFound.length === 0) {
      toast.error('Aucun module a importer');
      return;
    }
    const existingIds = formData.modules_formation.map(mf => mf.module_id);
    let added = 0;
    const newMfs = [...formData.modules_formation];
    for (const f of allFound) {
      const dbMod = f.db_module;
      if (dbMod && !existingIds.includes(dbMod.id)) {
        newMfs.push({
          module_id: dbMod.id,
          duree_heures: f.duree_heures || dbMod.duree_heures || 7,
          ordre: newMfs.length + 1,
        });
        existingIds.push(dbMod.id);
        added++;
      }
    }
    setFormData(prev => ({ ...prev, modules_formation: newMfs }));
    toast.success(`${added} module(s) ajoute(s) a la formation`);
    setImportDialogOpen(false);
    setImportResult(null);
  };

  // Extract unique prefixes from module codes for filter
  const moduleCategories = React.useMemo(() => {
    const prefixes = new Map();
    modules.forEach(m => {
      const match = m.code.match(/^([A-Z]+)/);
      if (match) {
        const p = match[1];
        prefixes.set(p, (prefixes.get(p) || 0) + 1);
      }
    });
    return [...prefixes.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  }, [modules]);

  // Filter modules
  const filteredModulesForCheckbox = React.useMemo(() => {
    return modules.filter(m => {
      if (moduleFilter !== 'all') {
        const match = m.code.match(/^([A-Z]+)/);
        if (!match || match[1] !== moduleFilter) return false;
      }
      if (moduleSearch) {
        const q = moduleSearch.toLowerCase();
        return m.code.toLowerCase().includes(q) || m.titre.toLowerCase().includes(q);
      }
      return true;
    });
  }, [modules, moduleFilter, moduleSearch]);

  const toggleModuleInFormation = (moduleId) => {
    const exists = formData.modules_formation.some(mf => mf.module_id === moduleId);
    if (exists) {
      removeModuleFromFormation(moduleId);
    } else {
      const mod = modules.find(m => m.id === moduleId);
      if (!mod) return;
      const newMf = {
        module_id: moduleId,
        duree_heures: 7,
        ordre: formData.modules_formation.length + 1
      };
      setFormData(prev => ({ ...prev, modules_formation: [...prev.modules_formation, newMf] }));
    }
  };

  // Computed values
  const [dragModuleIdx, setDragModuleIdx] = useState(null);
  const [dragOverIdx, setDragOverIdx] = useState(null);

  const handleModuleDragStart = (e, idx) => {
    setDragModuleIdx(idx);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleModuleDragOver = (e, idx) => {
    e.preventDefault();
    setDragOverIdx(idx);
  };

  const handleModuleDrop = (e, dropIdx) => {
    e.preventDefault();
    if (dragModuleIdx === null || dragModuleIdx === dropIdx) {
      setDragModuleIdx(null);
      setDragOverIdx(null);
      return;
    }
    const sorted = [...formData.modules_formation].sort((a, b) => a.ordre - b.ordre);
    const [moved] = sorted.splice(dragModuleIdx, 1);
    sorted.splice(dropIdx, 0, moved);
    const reordered = sorted.map((mf, i) => ({ ...mf, ordre: i + 1 }));
    setFormData(prev => ({ ...prev, modules_formation: reordered }));
    setDragModuleIdx(null);
    setDragOverIdx(null);
  };

  const totalModulesHeures = formData.modules_formation.reduce((sum, mf) => sum + mf.duree_heures, 0);
  const dureeMismatch = formData.duree_totale_heures > 0 && totalModulesHeures !== formData.duree_totale_heures;

  const getModuleInfo = (moduleId) => {
    const mod = modules.find(m => m.id === moduleId);
    return mod || { code: '?', titre: moduleId, duree_heures: 0 };
  };

  const getDomaineInfo = useCallback((domaineId) => {
    return domaines.find(d => d.id === domaineId);
  }, [domaines]);

  const getTypeLabel = (type) => {
    const found = typesFormation.find(t => t.value === type);
    return found ? found.label : type;
  };

  const toggleSort = (field) => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const filteredFormations = React.useMemo(() => {
    let result = formations.filter(f => {
      const q = searchTerm.toLowerCase();
      if (q && !f.titre.toLowerCase().includes(q) && !f.code.toLowerCase().includes(q)) return false;
      if (filterDomaine !== 'all' && (f.domaine_id || '') !== filterDomaine) return false;
      if (filterType !== 'all' && f.type_formation !== filterType) return false;
      if (filterCertif !== 'all' && (f.type_certification || 'qualifiante') !== filterCertif) return false;
      if (filterDuree !== 'all' && Number(f.duree_totale_heures || 0) !== Number(filterDuree)) return false;
      if (filterDureeMin !== '' && (f.duree_totale_heures || 0) < Number(filterDureeMin)) return false;
      if (filterDureeMax !== '' && (f.duree_totale_heures || 0) > Number(filterDureeMax)) return false;
      return true;
    });
    result.sort((a, b) => {
      let vA, vB;
      switch (sortField) {
        case 'code': vA = a.code || ''; vB = b.code || ''; break;
        case 'titre': vA = a.titre || ''; vB = b.titre || ''; break;
        case 'domaine': vA = getDomaineInfo(a.domaine_id)?.nom || ''; vB = getDomaineInfo(b.domaine_id)?.nom || ''; break;
        case 'type': vA = a.type_formation || ''; vB = b.type_formation || ''; break;
        case 'duree': return sortDirection === 'asc' ? (a.duree_totale_heures || 0) - (b.duree_totale_heures || 0) : (b.duree_totale_heures || 0) - (a.duree_totale_heures || 0);
        case 'modules': {
          const nA = a.modules_formation?.length || a.modules?.length || 0;
          const nB = b.modules_formation?.length || b.modules?.length || 0;
          return sortDirection === 'asc' ? nA - nB : nB - nA;
        }
        case 'competences': {
          const nA = competencesTitres(a).length, nB = competencesTitres(b).length;
          return sortDirection === 'asc' ? nA - nB : nB - nA;
        }
        default: vA = ''; vB = '';
      }
      return sortDirection === 'asc' ? vA.localeCompare(vB) : vB.localeCompare(vA);
    });
    return result;
  }, [formations, searchTerm, filterDomaine, filterType, filterCertif, filterDuree, filterDureeMin, filterDureeMax, sortField, sortDirection, getDomaineInfo]);

  const dureesDisponibles = React.useMemo(() => [...new Set(formations.map(f => Number(f.duree_totale_heures || 0)).filter(h => h > 0))].sort((a, b) => a - b), [formations]);

  // Auto-update code when relevant fields change (only for new formations)
  const handleFieldChange = (field, value) => {
    const updated = { ...formData, [field]: value };
    if (!editingFormation && ['domaine_id', 'titre', 'duree_totale_heures'].includes(field)) {
      const newCode = generateCode(updated);
      if (newCode) updated.code = newCode;
    }
    setFormData(updated);
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="formations-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 font-['Plus_Jakarta_Sans']">Formations</h1>
            <p className="text-slate-500 mt-1">{formations.length} formation(s)</p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => setDomainesManagerOpen(true)} data-testid="btn-manage-domaines">
              <FolderTree className="w-4 h-4 mr-2" />
              Domaines
            </Button>
            <Button onClick={() => handleOpenDialog()} className="bg-indigo-600 hover:bg-indigo-700" data-testid="new-formation-btn">
              <Plus className="w-4 h-4 mr-2" />
              Nouvelle formation
            </Button>
          </div>
        </div>

        <DomainesDialog
          open={domainesManagerOpen}
          onOpenChange={setDomainesManagerOpen}
          domaines={domaines}
          formations={formations}
          onChanged={fetchData}
        />

        {/* Search & Filters */}
        <div className="flex flex-wrap items-end gap-3">
          <div className="relative min-w-[220px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Rechercher..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 h-9 text-sm"
              data-testid="search-formations"
            />
          </div>
          <Select value={filterDomaine} onValueChange={setFilterDomaine}>
            <SelectTrigger className="h-9 text-xs w-[160px]" data-testid="filter-domaine"><SelectValue placeholder="Domaine" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous domaines</SelectItem>
              {domaines.map(d => <SelectItem key={d.id} value={d.id}>{d.nom}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filterType} onValueChange={setFilterType}>
            <SelectTrigger className="h-9 text-xs w-[140px]" data-testid="filter-type"><SelectValue placeholder="Type" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous types</SelectItem>
              {typesFormation.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filterCertif} onValueChange={setFilterCertif}>
            <SelectTrigger className="h-9 text-xs w-[150px]" data-testid="filter-certif"><SelectValue placeholder="Certification" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Toutes</SelectItem>
              <SelectItem value="qualifiante">Qualifiante</SelectItem>
              <SelectItem value="certifiante">Certifiante</SelectItem>
            </SelectContent>
          </Select>
          <Select value={filterDuree} onValueChange={setFilterDuree}>
            <SelectTrigger className="h-9 text-xs w-[130px]" data-testid="filter-duree"><SelectValue placeholder="Durée" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Toutes durées</SelectItem>
              {dureesDisponibles.map(h => <SelectItem key={h} value={String(h)}>{h} h{h >= 7 ? ` (${Math.round(h / 7 * 10) / 10} j)` : ''}</SelectItem>)}
            </SelectContent>
          </Select>
          <div className="flex items-center gap-1">
            <Input
              type="number"
              placeholder="Min h"
              value={filterDureeMin}
              onChange={(e) => setFilterDureeMin(e.target.value)}
              className="h-9 text-xs w-[70px]"
              data-testid="filter-duree-min"
            />
            <span className="text-xs text-slate-400">-</span>
            <Input
              type="number"
              placeholder="Max h"
              value={filterDureeMax}
              onChange={(e) => setFilterDureeMax(e.target.value)}
              className="h-9 text-xs w-[70px]"
              data-testid="filter-duree-max"
            />
          </div>
          {(filterDomaine !== 'all' || filterType !== 'all' || filterCertif !== 'all' || filterDuree !== 'all' || filterDureeMin !== '' || filterDureeMax !== '') && (
            <Button variant="ghost" size="sm" className="h-9 text-xs text-slate-500" onClick={() => { setFilterDomaine('all'); setFilterType('all'); setFilterCertif('all'); setFilterDuree('all'); setFilterDureeMin(''); setFilterDureeMax(''); }}>
              <X className="w-3 h-3 mr-1" />Réinitialiser
            </Button>
          )}
          <span className="text-xs text-slate-400 ml-auto whitespace-nowrap" data-testid="formations-count">{filteredFormations.length}/{formations.length} formation(s)</span>
        </div>

        {/* Formations List */}
        {loading ? (
          <Card><CardContent className="p-6 space-y-3">{[1, 2, 3].map((i) => (<div key={i} className="h-10 bg-slate-100 rounded animate-pulse" />))}</CardContent></Card>
        ) : filteredFormations.length === 0 ? (
          <Card className="border-dashed">
            <CardContent className="py-12 text-center">
              <GraduationCap className="w-12 h-12 mx-auto text-slate-300 mb-4" />
              <h3 className="text-lg font-medium text-slate-900 mb-1">Aucune formation</h3>
              <p className="text-slate-500 mb-4">{searchTerm ? 'Aucun résultat' : 'Créez votre première formation'}</p>
              {!searchTerm && (
                <Button onClick={() => handleOpenDialog()} className="bg-indigo-600 hover:bg-indigo-700">
                  <Plus className="w-4 h-4 mr-2" />Créer une formation
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          <Card className="border-0 shadow-sm overflow-hidden">
            <div className="grid grid-cols-[100px_1fr_minmax(0,1.2fr)_120px_80px_80px_80px] gap-4 bg-slate-100 border-b px-5 py-2.5 text-xs font-medium text-slate-500 uppercase tracking-wide">
              {[
                { key: 'code', label: 'Code', className: '' },
                { key: 'titre', label: 'Titre', className: '' },
                { key: 'competences', label: 'Compétences à atteindre', className: 'hidden md:flex' },
                { key: 'domaine', label: 'Domaine', className: 'hidden md:flex' },
                { key: 'type', label: 'Type', className: 'hidden md:flex' },
                { key: 'duree', label: 'Durée', className: 'hidden md:flex justify-center' },
                { key: 'modules', label: 'Modules', className: 'hidden lg:flex justify-center' },
              ].map(col => (
                <button
                  key={col.key}
                  type="button"
                  className={`flex items-center gap-1 hover:text-slate-700 transition-colors cursor-pointer select-none ${col.className}`}
                  onClick={() => toggleSort(col.key)}
                  data-testid={`sort-${col.key}`}
                >
                  {col.label}
                  {sortField === col.key ? (
                    sortDirection === 'asc' ? <ChevronUp className="w-3 h-3 text-indigo-500" /> : <ChevronDown className="w-3 h-3 text-indigo-500" />
                  ) : (
                    <ChevronDown className="w-3 h-3 opacity-0 group-hover:opacity-30" />
                  )}
                </button>
              ))}
            </div>
            <div className="divide-y">
              {filteredFormations.map((formation) => {
                const domaine = getDomaineInfo(formation.domaine_id);
                const nbModules = formation.modules_formation?.length || formation.modules?.length || 0;
                const cps = competencesTitres(formation);
                return (
                  <div
                    key={formation.id}
                    className="grid grid-cols-[100px_1fr_minmax(0,1.2fr)_120px_80px_80px_80px] gap-4 items-center px-5 py-3 hover:bg-slate-50 cursor-pointer transition-colors"
                    data-testid={`formation-row-${formation.id}`}
                    onClick={() => setSelectedFormation(formation)}
                  >
                    <span className="text-xs font-mono text-emerald-600 font-medium">{formation.code}</span>
                    <div className="min-w-0 flex items-center gap-2">
                      <p className="text-sm font-medium text-slate-900 truncate">{formation.titre}</p>
                      {formation.est_certifiante && (
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 text-[10px] font-semibold flex-shrink-0" data-testid={`badge-rncp-${formation.id}`}>
                          <Award className="w-3 h-3" />
                          {(formation.certifications || []).map(c => c.code_rncp).join(', ') || 'Certifiante'}
                        </span>
                      )}
                    </div>
                    <div className="min-w-0 hidden md:block" title={cps.map((t, i) => `C${i + 1}. ${t}`).join('\n')} data-testid={`formation-competences-${formation.id}`}>
                      {cps.length === 0 ? <span className="text-xs text-slate-300">—</span> : (
                        <ul className="space-y-0.5">
                          {cps.slice(0, 2).map((t, i) => <li key={i} className="text-xs text-slate-600 truncate"><span className="text-emerald-600 font-semibold mr-1">C{i + 1}</span>{t}</li>)}
                          {cps.length > 2 && <li className="text-[11px] text-slate-400">+ {cps.length - 2} autre(s) — {cps.length} compétences au total</li>}
                        </ul>
                      )}
                    </div>
                    <span className="text-xs text-slate-500 truncate hidden md:block">{domaine?.nom || '-'}</span>
                    <div className="hidden md:block">
                      <StatusBadge status={formation.type_formation} />
                    </div>
                    <span className="text-sm text-slate-600 text-center hidden md:block">{formation.duree_totale_heures}h</span>
                    <span className="text-sm text-slate-500 text-center hidden lg:block">{nbModules}</span>
                  </div>
                );
              })}
            </div>
          </Card>
        )}

        {/* Formation Detail Dialog */}
        <Dialog open={!!selectedFormation} onOpenChange={(open) => !open && setSelectedFormation(null)}>
          <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
            {selectedFormation && (
              <>
                <DialogHeader>
                  <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div>
                      <span className="inline-flex items-center px-2.5 py-1 rounded-md text-sm font-mono font-semibold bg-emerald-100 text-emerald-700 mb-2">
                        {selectedFormation.code}
                      </span>
                      <DialogTitle className="text-2xl font-['Plus_Jakarta_Sans']">{selectedFormation.titre}</DialogTitle>
                    </div>
                    <div className="flex items-center gap-2 pr-8">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="gap-1.5 text-emerald-700 border-emerald-200 hover:bg-emerald-50"
                        disabled={downloadingPlaquette !== null}
                        onClick={() => downloadPlaquette(selectedFormation, false)}
                        data-testid="btn-plaquette-simple"
                        title="Télécharger la plaquette de formation (version simple)"
                      >
                        {downloadingPlaquette === 'simple' ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileDown className="w-4 h-4" />}
                        Plaquette simple
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="gap-1.5 text-indigo-700 border-indigo-200 hover:bg-indigo-50"
                        disabled={downloadingPlaquette !== null}
                        onClick={() => downloadPlaquette(selectedFormation, true)}
                        data-testid="btn-plaquette-detaillee"
                        title="Télécharger la plaquette détaillée (avec le détail de chaque module)"
                      >
                        {downloadingPlaquette === 'detaillee' ? <Loader2 className="w-4 h-4 animate-spin" /> : <BookOpenText className="w-4 h-4" />}
                        Plaquette détaillée
                      </Button>
                    </div>
                  </div>
                </DialogHeader>
                
                <div className="space-y-4 py-4">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <InfoBlock icon={GraduationCap} label="Type" value={getTypeLabel(selectedFormation.type_formation)} />
                    <InfoBlock icon={Clock} label="Durée" value={`${selectedFormation.duree_totale_heures}h`} />
                    <InfoBlock icon={BookOpen} label="Modules" value={`${selectedFormation.modules_formation?.length || selectedFormation.modules?.length || 0}`} />
                    <InfoBlock icon={Award} label="Certification" value={selectedFormation.est_certifiante ? `Certifiante (${(selectedFormation.certifications || []).map(c => c.code_rncp).join(', ')})` : 'Qualifiante'} />
                  </div>
                  
                  {selectedFormation.domaine_id && (
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-medium text-slate-500">Domaine :</span>
                      <span className="text-sm text-slate-700">{getDomaineInfo(selectedFormation.domaine_id)?.nom || '-'}</span>
                    </div>
                  )}

                  {selectedFormation.code_nsf && (
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-medium text-slate-500">Code NSF :</span>
                      <span className="text-sm font-mono text-slate-700">{selectedFormation.code_nsf}</span>
                    </div>
                  )}

                  {(selectedFormation.certifications || []).map(cd => {
                    const cert = certifications.find(c => c.id === cd.id) || cd;
                    return (
                      <div key={cd.id} className="flex items-center justify-between bg-amber-50 rounded-lg px-3 py-2">
                        <div className="flex items-center gap-2">
                          <Award className="w-4 h-4 text-amber-600" />
                          <span className="text-xs font-mono text-amber-700">{cert.code_rncp}</span>
                          <span className="text-sm font-medium text-amber-900">{cert.titre}</span>
                        </div>
                        {certifications.find(c => c.id === cd.id) && (
                          <button
                            onClick={() => setViewingCertification(certifications.find(c => c.id === cd.id))}
                            className="text-xs text-amber-700 hover:text-amber-900 underline"
                          >
                            Voir la fiche
                          </button>
                        )}
                      </div>
                    );
                  })}

                  {selectedFormation.objectif_pedagogique && (
                    <DetailSection icon={FileText} title="Objectif pédagogique" content={selectedFormation.objectif_pedagogique} />
                  )}

                  {(selectedFormation.competences_pro?.length > 0 || selectedFormation.competences_professionnelles) && (
                    <div className="p-3 bg-slate-50 rounded-lg" data-testid="detail-competences-pro">
                      <div className="flex items-center gap-2 mb-2"><Shield className="w-4 h-4 text-slate-600" /><span className="text-sm font-semibold text-slate-700">Compétences professionnelles à valider</span></div>
                      <CompetencesProList competences={selectedFormation.competences_pro} legacyText={selectedFormation.competences_professionnelles} />
                    </div>
                  )}

                  {/* Nouveaux champs */}
                  {(selectedFormation.niveau_acces || selectedFormation.niveau_sortie) && (
                    <div className="grid grid-cols-2 gap-3">
                      {selectedFormation.niveau_acces && <InfoBlock icon={GraduationCap} label="Niveau d'acces" value={selectedFormation.niveau_acces} />}
                      {selectedFormation.niveau_sortie && <InfoBlock icon={Award} label="Niveau de sortie" value={selectedFormation.niveau_sortie} />}
                    </div>
                  )}

                  {selectedFormation.metiers && (
                    <DetailSection icon={Briefcase} title="Pour quels metiers ?" content={selectedFormation.metiers} />
                  )}

                  {selectedFormation.programme && (
                    <DetailSection icon={BookOpen} title="Programme" content={selectedFormation.programme} />
                  )}

                  {selectedFormation.plus_pedagogiques && (
                    <DetailSection icon={CheckCircle} title="Les + pedagogiques" content={selectedFormation.plus_pedagogiques} />
                  )}

                  {selectedFormation.modalites_organisation && (
                    <DetailSection icon={FileText} title="Modalites d'organisation" content={selectedFormation.modalites_organisation} />
                  )}

                  {selectedFormation.modalites_evaluation && (
                    <DetailSection icon={Shield} title="Modalites d'evaluation" content={selectedFormation.modalites_evaluation} />
                  )}

                  {(selectedFormation.tarif_inter || selectedFormation.tarif_intra || selectedFormation.lieu_formation) && (
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                      {selectedFormation.tarif_inter != null && <InfoBlock icon={FileText} label="Tarif inter" value={`${Number(selectedFormation.tarif_inter).toLocaleString('fr-FR')} EUR HT`} />}
                      {selectedFormation.tarif_intra != null && <InfoBlock icon={FileText} label="Tarif intra" value={`${Number(selectedFormation.tarif_intra).toLocaleString('fr-FR')} EUR HT`} />}
                      {selectedFormation.lieu_formation && <InfoBlock icon={MapPin} label="Lieu de formation" value={selectedFormation.lieu_formation} />}
                    </div>
                  )}

                  {/* Modules */}
                  <div className="p-3 bg-emerald-50 rounded-lg">
                    <div className="flex items-center gap-2 mb-3">
                      <BookOpen className="w-5 h-5 text-emerald-600" />
                      <p className="text-sm font-medium text-emerald-900">Modules de la formation</p>
                    </div>
                    {(selectedFormation.modules_formation?.length > 0) ? (
                      <div className="space-y-1.5">
                        {[...selectedFormation.modules_formation].sort((a, b) => a.ordre - b.ordre).map((mf) => {
                          const mod = getModuleInfo(mf.module_id);
                          return (
                            <div key={mf.module_id} className="flex items-center gap-3 p-2 bg-white rounded border border-emerald-200">
                              <span className="text-xs font-bold text-emerald-600 w-5">{mf.ordre}</span>
                              <span className="text-xs font-mono text-emerald-600">{mod.code}</span>
                              <span className="text-sm text-slate-700 flex-1">{mod.titre}</span>
                              <span className="text-xs text-slate-500">{mf.duree_heures}h</span>
                            </div>
                          );
                        })}
                        <div className="text-right text-xs font-medium text-emerald-700 mt-2">
                          Total : {selectedFormation.modules_formation.reduce((s, m) => s + m.duree_heures, 0)}h / {selectedFormation.duree_totale_heures}h
                        </div>
                        <div className="mt-3 bg-white rounded-lg">
                          <DecoupageJournees
                            modulesFormation={[...selectedFormation.modules_formation].sort((a, b) => a.ordre - b.ordre)}
                            getModuleInfo={getModuleInfo}
                            heuresParJour={selectedFormation.heures_par_jour || 7}
                            decoupage={selectedFormation.decoupage_journees || null}
                          />
                        </div>
                      </div>
                    ) : selectedFormation.modules?.length > 0 ? (
                      <div className="space-y-1.5">
                        {selectedFormation.modules.map((moduleId) => {
                          const mod = getModuleInfo(moduleId);
                          return (
                            <div key={moduleId} className="flex items-center gap-2 p-2 bg-white rounded border border-emerald-200">
                              <span className="text-xs font-mono text-emerald-600">{mod.code}</span>
                              <span className="text-sm text-slate-700">{mod.titre}</span>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <p className="text-sm text-emerald-600">Aucun module assigné</p>
                    )}
                  </div>
                </div>

                {/* Actions de formation liées */}
                <div className="border-t pt-4">
                  <div className="flex items-center gap-2 mb-3">
                    <Briefcase className="w-4 h-4 text-indigo-500" />
                    <span className="text-sm font-semibold text-slate-700">Actions de formation ({formationActions.length})</span>
                  </div>
                  {formationActions.length === 0 ? (
                    <p className="text-sm text-slate-400 italic">Aucune action de formation n'utilise cette formation.</p>
                  ) : (
                    <div className="space-y-1.5">
                      {formationActions.map(action => (
                        <div
                          key={action.id}
                          className="flex items-center justify-between px-3 py-2 rounded-md border border-slate-200 hover:border-indigo-300 hover:bg-indigo-50/50 cursor-pointer transition-colors group"
                          onClick={() => { setSelectedFormation(null); navigate(`/dossiers/${action.id}`); }}
                          data-testid={`action-link-${action.id}`}
                        >
                          <div className="flex items-center gap-3">
                            <span className="text-xs font-mono text-indigo-600 bg-indigo-50 px-1.5 py-0.5 rounded">{action.code}</span>
                            <span className="text-sm text-slate-700 font-medium">{action.intitule}</span>
                            <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${
                              action.statut === 'en_cours' ? 'bg-blue-100 text-blue-700' :
                              action.statut === 'termine' ? 'bg-emerald-100 text-emerald-700' :
                              action.statut === 'abandon' ? 'bg-red-100 text-red-700' : 'bg-slate-100 text-slate-600'
                            }`}>
                              {action.statut === 'en_cours' ? 'En cours' : action.statut === 'termine' ? 'Terminé' : action.statut === 'abandon' ? 'Abandon' : action.statut || 'En cours'}
                            </span>
                          </div>
                          <div className="flex items-center gap-3 text-xs text-slate-500">
                            {action.date_debut && <span>{action.date_debut}</span>}
                            <span>{action.candidats?.length || 0} candidat(s)</span>
                            <span className="text-indigo-500 opacity-0 group-hover:opacity-100">Ouvrir →</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <DialogFooter className="flex gap-2 sm:gap-2">
                  <Button variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" onClick={() => { setDeleteId(selectedFormation.id); }}>
                    <Trash2 className="w-4 h-4 mr-2" />Supprimer
                  </Button>
                  <Button variant="outline" className="text-indigo-600 border-indigo-200 hover:bg-indigo-50" onClick={() => handleClone(selectedFormation)} data-testid="clone-formation-btn">
                    <Copy className="w-4 h-4 mr-2" />Cloner
                  </Button>
                  <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={() => { setSelectedFormation(null); handleOpenDialog(selectedFormation); }}>
                    <Pencil className="w-4 h-4 mr-2" />Modifier
                  </Button>
                </DialogFooter>
              </>
            )}
          </DialogContent>
        </Dialog>

        {/* Formation Create/Edit Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-[1400px] w-[95vw] max-h-[92vh] overflow-y-auto" data-testid="formation-dialog">
            <DialogHeader>
              <DialogTitle className="font-['Plus_Jakarta_Sans']">
                {editingFormation ? 'Modifier la formation' : 'Nouvelle formation'}
              </DialogTitle>
              <DialogDescription>
                {editingFormation ? 'Modifiez les informations de la formation' : 'Créez un nouveau programme de formation'}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-5">
              <Tabs value={formTab} onValueChange={setFormTab} className="w-full">
              <div className="flex items-center justify-between gap-4 -mt-2">
                <TabsList className="bg-slate-100">
                  <TabsTrigger value="general" data-testid="tab-formation-general">Informations générales</TabsTrigger>
                  <TabsTrigger value="modules" data-testid="tab-formation-modules">
                    Modules & découpage ({formData.modules_formation.length})
                  </TabsTrigger>
                </TabsList>
                <Button type="submit" size="sm" className="bg-indigo-600 hover:bg-indigo-700" data-testid="submit-formation-top">
                  {editingFormation ? 'Mettre à jour' : 'Créer'}
                </Button>
              </div>
              <TabsContent value="general" className="space-y-5 mt-4">
              {/* Row 1: Domaine + Type */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Domaine *</Label>
                  <div className="flex gap-2">
                    <Select value={formData.domaine_id || "none"} onValueChange={(v) => handleFieldChange('domaine_id', v === 'none' ? '' : v)}>
                      <SelectTrigger data-testid="select-domaine"><SelectValue placeholder="Sélectionner..." /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none"><span className="text-slate-400">Sélectionner un domaine</span></SelectItem>
                        {domaines.map(d => (
                          <SelectItem key={d.id} value={d.id}>{d.nom} ({d.abreviation})</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button type="button" variant="outline" size="icon" className="flex-shrink-0" onClick={() => setDomaineDialogOpen(true)} data-testid="add-domaine-btn">
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Type de formation</Label>
                  <Select value={formData.type_formation} onValueChange={(v) => setFormData({ ...formData, type_formation: v })}>
                    <SelectTrigger data-testid="select-type-formation"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {typesFormation.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Row 2: Titre */}
              <div className="space-y-2">
                <Label>Titre de la formation *</Label>
                <Input
                  value={formData.titre}
                  onChange={(e) => handleFieldChange('titre', e.target.value)}
                  placeholder="Ex: Préparateur de commande"
                  required
                  data-testid="input-formation-titre"
                />
              </div>

              {/* Row 3: Code + Durée */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Code (auto-généré, modifiable)</Label>
                  <Input
                    value={formData.code}
                    onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                    placeholder="Ex: FLOG-PC35"
                    required
                    data-testid="input-formation-code"
                  />
                  <p className="text-[10px] text-slate-400">Format : F + domaine + - + titre abrégé + durée</p>
                </div>
                <div className="space-y-2">
                  <Label>Durée totale (heures) *</Label>
                  <Input
                    type="number"
                    value={formData.duree_totale_heures}
                    onChange={(e) => handleFieldChange('duree_totale_heures', parseInt(e.target.value) || 0)}
                    min="0"
                    required
                    data-testid="input-formation-duree"
                  />
                </div>
              </div>

              {/* Row 4: Certification (dérivée des modules) & Code NSF */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Type de certification</Label>
                  <div className="flex items-center gap-2 h-10 px-3 rounded-md border border-slate-200 bg-slate-50" data-testid="certification-derivee">
                    {editingFormation?.est_certifiante ? (
                      <>
                        <Award className="w-4 h-4 text-amber-500" />
                        <span className="text-sm font-medium text-amber-700">Certifiante</span>
                      </>
                    ) : (
                      <span className="text-sm text-slate-500">Qualifiante</span>
                    )}
                  </div>
                  <p className="text-[11px] text-slate-400">
                    Déterminé automatiquement : la formation devient certifiante dès qu'elle contient au moins un module certifiant (Support pédagogique → module → "Certification préparée")
                  </p>
                </div>
                <div className="space-y-2">
                  <Label>Code NSF</Label>
                  <Select
                    value={formData.code_nsf || 'none'}
                    onValueChange={(v) => setFormData({ ...formData, code_nsf: v === 'none' ? '' : v })}
                  >
                    <SelectTrigger data-testid="select-nsf"><SelectValue placeholder="Sélectionner un code NSF..." /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none"><span className="text-slate-400">Aucun</span></SelectItem>
                      {codesNsf.map(nsf => (
                        <SelectItem key={nsf.code} value={nsf.code}>{nsf.code} — {nsf.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Certifications dérivées des modules certifiants */}
              {(editingFormation?.certifications || []).length > 0 && (
                <div className="border border-amber-200 bg-amber-50/40 rounded-lg p-4 space-y-2" data-testid="certifications-modules">
                  <div className="flex items-center gap-2">
                    <Award className="w-4 h-4 text-amber-600" />
                    <Label className="text-sm font-semibold text-amber-700">Certifications préparées (via modules certifiants)</Label>
                  </div>
                  {editingFormation.certifications.map(cd => {
                    const cert = certifications.find(c => c.id === cd.id) || cd;
                    return (
                      <div key={cd.id} className="flex items-center justify-between bg-white border border-amber-100 rounded-md px-4 py-2.5">
                        <div className="flex items-center gap-3">
                          <span className="text-xs font-mono text-amber-700 bg-amber-100 px-2 py-0.5 rounded">{cert.code_rncp}</span>
                          <span className="text-sm font-medium text-slate-800">{cert.titre}</span>
                        </div>
                        {certifications.find(c => c.id === cd.id) && (
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="text-xs border-amber-300 text-amber-700 hover:bg-amber-50"
                            onClick={() => setViewingCertification(certifications.find(c => c.id === cd.id))}
                            data-testid="btn-view-certification"
                          >
                            <FileText className="w-3.5 h-3.5 mr-1" />
                            Voir la fiche
                          </Button>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Row 5: Objectif pédagogique */}
              <div className="space-y-2">
                <Label>Objectif pédagogique</Label>
                <Textarea
                  value={formData.objectif_pedagogique}
                  onChange={(e) => setFormData({ ...formData, objectif_pedagogique: e.target.value })}
                  placeholder="Décrivez l'objectif pédagogique de la formation..."
                  rows={3}
                  data-testid="input-objectif"
                />
              </div>

              {/* Row 6: Compétences */}
              <div className="space-y-2">
                <Label>Compétences professionnelles à valider <span className="text-xs font-normal text-slate-400">(validées atteinte / non atteinte dans le bilan)</span></Label>
                <CompetencesProEditor value={formData.competences_pro} onChange={(v) => setFormData({ ...formData, competences_pro: v })} />
                {formData.competences_professionnelles && formData.competences_pro.length === 0 && (
                  <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
                    Ancien texte : « {formData.competences_professionnelles.slice(0, 120)}{formData.competences_professionnelles.length > 120 ? '…' : ''} »
                    <button type="button" className="ml-2 underline" data-testid="cp-convert-legacy" onClick={() => setFormData({ ...formData, competences_pro: formData.competences_professionnelles.split('\n').map(l => l.replace(/^[-•*\s]+/, '').trim()).filter(Boolean).map(t => ({ id: crypto.randomUUID(), titre: t, sous_competences: [] })) })}>Convertir en liste</button>
                  </div>
                )}
              </div>

              {/* Niveaux */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Niveau d'acces</Label>
                  <Input value={formData.niveau_acces} onChange={e => setFormData({...formData, niveau_acces: e.target.value})}
                    placeholder="Ex: Aucun prerequis, Niveau Bac..." data-testid="input-niveau-acces" />
                </div>
                <div className="space-y-2">
                  <Label>Niveau de sortie</Label>
                  <Input value={formData.niveau_sortie} onChange={e => setFormData({...formData, niveau_sortie: e.target.value})}
                    placeholder="Ex: Niveau 3, Attestation de competences..." data-testid="input-niveau-sortie" />
                </div>
              </div>

              {/* Metiers */}
              <div className="space-y-2">
                <Label>Pour quels metiers ?</Label>
                <Textarea value={formData.metiers} onChange={e => setFormData({...formData, metiers: e.target.value})}
                  placeholder="Listez les metiers vises par cette formation..." rows={2} data-testid="input-metiers" />
              </div>

              {/* Programme */}
              <div className="space-y-2">
                <Label>Programme</Label>
                <Textarea value={formData.programme} onChange={e => setFormData({...formData, programme: e.target.value})}
                  placeholder="Decrivez le programme detaille de la formation..." rows={4} data-testid="input-programme" />
              </div>

              {/* Les + pedagogiques */}
              <div className="space-y-2">
                <Label>Les + pedagogiques</Label>
                <Textarea value={formData.plus_pedagogiques} onChange={e => setFormData({...formData, plus_pedagogiques: e.target.value})}
                  placeholder="Ex: Mise en situation, cas pratiques, formateur expert..." rows={2} data-testid="input-plus-pedago" />
              </div>

              {/* Modalites */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Modalites d'organisation</Label>
                  <Textarea value={formData.modalites_organisation} onChange={e => setFormData({...formData, modalites_organisation: e.target.value})}
                    placeholder="Ex: Groupes de 6 a 12 personnes, horaires 9h-17h..." rows={2} data-testid="input-modalites-orga" />
                </div>
                <div className="space-y-2">
                  <Label>Modalites d'evaluation</Label>
                  <Textarea value={formData.modalites_evaluation} onChange={e => setFormData({...formData, modalites_evaluation: e.target.value})}
                    placeholder="Ex: QCM, mise en situation, evaluation continue..." rows={2} data-testid="input-modalites-eval" />
                </div>
              </div>

              {/* Tarifs et Lieu */}
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Tarif inter (EUR HT)</Label>
                  <Input type="number" value={formData.tarif_inter} onChange={e => setFormData({...formData, tarif_inter: e.target.value})}
                    placeholder="0" data-testid="input-tarif-inter" />
                </div>
                <div className="space-y-2">
                  <Label>Tarif intra (EUR HT)</Label>
                  <Input type="number" value={formData.tarif_intra} onChange={e => setFormData({...formData, tarif_intra: e.target.value})}
                    placeholder="0" data-testid="input-tarif-intra" />
                </div>
                <div className="space-y-2">
                  <Label>Lieu de formation</Label>
                  <Input value={formData.lieu_formation} onChange={e => setFormData({...formData, lieu_formation: e.target.value})}
                    placeholder="Ex: Sur site, A distance, Centre de formation..." data-testid="input-lieu-formation" />
                </div>
              </div>

              </TabsContent>

              <TabsContent value="modules" className="mt-4">
              {/* Row 7: Modules */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="text-base">Modules de la formation</Label>
                  <div className="flex items-center gap-3">
                    <span className={`text-xs font-medium ${dureeMismatch ? 'text-red-600' : 'text-emerald-600'}`}>
                      {totalModulesHeures}h / {formData.duree_totale_heures}h
                      {dureeMismatch && ' (ne correspond pas)'}
                    </span>
                    <label className="cursor-pointer">
                      <input type="file" accept=".xlsx,.xls,.csv" className="hidden" onChange={handleImportFile} data-testid="import-modules-input" />
                      <span className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md border border-indigo-200 text-indigo-600 bg-indigo-50 hover:bg-indigo-100 transition-colors">
                        {importLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
                        Importer (Excel/CSV)
                      </span>
                    </label>
                  </div>
                </div>

                <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,2fr)_minmax(0,3fr)] gap-5 items-start">
                <div className="space-y-3">
                <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">Catalogue des modules</p>
                {/* Category filter pills */}
                <div className="flex flex-wrap gap-1" data-testid="module-category-filter">
                  <button
                    type="button"
                    onClick={() => setModuleFilter('all')}
                    className={`px-2 py-0.5 rounded-full text-[10px] font-medium transition-colors ${
                      moduleFilter === 'all' ? 'bg-indigo-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                    }`}
                  >
                    Tous ({modules.length})
                  </button>
                  {moduleCategories.map(([prefix, count]) => (
                    <button
                      key={prefix}
                      type="button"
                      onClick={() => setModuleFilter(moduleFilter === prefix ? 'all' : prefix)}
                      className={`px-2 py-0.5 rounded-full text-[10px] font-medium transition-colors ${
                        moduleFilter === prefix ? 'bg-indigo-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                      }`}
                      data-testid={`filter-${prefix}`}
                    >
                      {prefix} ({count})
                    </button>
                  ))}
                </div>

                {/* Search within modules */}
                <div className="relative">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
                  <Input
                    placeholder="Rechercher un module..."
                    value={moduleSearch}
                    onChange={(e) => setModuleSearch(e.target.value)}
                    className="pl-8 h-8 text-xs"
                    data-testid="module-search-input"
                  />
                </div>

                {/* Checkbox list of modules */}
                <div className="border rounded-lg max-h-[420px] overflow-y-auto divide-y" data-testid="module-checkbox-list">
                  {filteredModulesForCheckbox.map(m => {
                    const mfEntry = formData.modules_formation.find(mf => mf.module_id === m.id);
                    const isChecked = !!mfEntry;
                    return (
                      <div
                        key={m.id}
                        className={`flex items-center gap-3 px-3 py-1.5 transition-colors ${
                          isChecked ? 'bg-indigo-50' : 'hover:bg-slate-50'
                        }`}
                        data-testid={`module-check-${m.id}`}
                      >
                        <Checkbox
                          checked={isChecked}
                          onCheckedChange={() => toggleModuleInFormation(m.id)}
                          className="shrink-0"
                        />
                        <span className="font-mono text-[10px] text-indigo-600 shrink-0 w-16">{m.code}</span>
                        <span className="text-xs text-slate-700 truncate flex-1">{m.titre}</span>
                        {isChecked ? (
                          <Input
                            type="number"
                            value={mfEntry.duree_heures}
                            onChange={(e) => updateModuleDuree(m.id, e.target.value)}
                            className="h-6 w-14 text-[11px] text-center shrink-0"
                            min="0"
                            onClick={(e) => e.stopPropagation()}
                            data-testid={`module-hours-${m.id}`}
                          />
                        ) : (
                          <span className="text-[10px] text-slate-400 shrink-0">{m.duree_heures || 7}h</span>
                        )}
                      </div>
                    );
                  })}
                  {filteredModulesForCheckbox.length === 0 && (
                    <div className="px-3 py-4 text-xs text-slate-400 text-center">Aucun module trouvé</div>
                  )}
                </div>
                </div>

                <div className="space-y-3">
                {formData.modules_formation.length === 0 && (
                  <div className="border border-dashed rounded-lg p-8 text-center text-sm text-slate-400" data-testid="no-modules-selected">
                    Cochez des modules dans le catalogue pour composer la formation.
                  </div>
                )}
                {/* Selected modules with drag & drop reordering */}
                {formData.modules_formation.length > 0 && (
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs font-medium text-slate-600">
                        {formData.modules_formation.length} module{formData.modules_formation.length > 1 ? 's' : ''} sélectionné{formData.modules_formation.length > 1 ? 's' : ''}
                        <span className="text-slate-400 ml-1">— glissez pour réordonner</span>
                      </span>
                    </div>
                    <div className="border rounded-lg overflow-hidden">
                      <div className="grid grid-cols-[28px_36px_1fr_80px_40px] gap-0 bg-slate-100 border-b px-2 py-1.5 text-[10px] font-medium text-slate-500 uppercase tracking-wide">
                        <span></span>
                        <span>Ordre</span>
                        <span>Module</span>
                        <span className="text-center">Durée (h)</span>
                        <span></span>
                      </div>
                      <div className="divide-y max-h-[250px] overflow-y-auto">
                        {[...formData.modules_formation].sort((a, b) => a.ordre - b.ordre).map((mf, idx) => {
                          const mod = getModuleInfo(mf.module_id);
                          const isDragging = dragModuleIdx === idx;
                          const isOver = dragOverIdx === idx;
                          return (
                            <div
                              key={mf.module_id}
                              draggable
                              onDragStart={(e) => handleModuleDragStart(e, idx)}
                              onDragOver={(e) => handleModuleDragOver(e, idx)}
                              onDragEnd={() => { setDragModuleIdx(null); setDragOverIdx(null); }}
                              onDrop={(e) => handleModuleDrop(e, idx)}
                              className={`grid grid-cols-[28px_36px_1fr_80px_40px] gap-0 items-center px-2 py-1.5 transition-colors cursor-grab active:cursor-grabbing ${
                                isDragging ? 'opacity-40 bg-indigo-50' :
                                isOver ? 'bg-indigo-50 border-t-2 border-indigo-400' :
                                'hover:bg-slate-50'
                              }`}
                              data-testid={`module-row-${mf.module_id}`}
                            >
                              <GripVertical className="w-3.5 h-3.5 text-slate-300" />
                              <span className="text-xs font-bold text-indigo-600">{mf.ordre}</span>
                              <div className="min-w-0">
                                <div className="truncate">
                                  <span className="text-[10px] font-mono text-emerald-600 mr-1">{mod.code}</span>
                                  <span className="text-sm text-slate-700">{mod.titre}</span>
                                </div>
                                <TestsLiesModule moduleId={mf.module_id} compact />
                              </div>
                              <Input
                                type="number"
                                value={mf.duree_heures}
                                onChange={(e) => updateModuleDuree(mf.module_id, e.target.value)}
                                className="h-7 text-xs text-center"
                                min="0"
                              />
                              <button type="button" onClick={() => removeModuleFromFormation(mf.module_id)} className="p-1 hover:bg-red-50 rounded text-red-400 hover:text-red-600 mx-auto">
                                <X className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                )}

                {/* Découpage par journée */}
                {formData.modules_formation.length > 0 && (
                  <DecoupageJournees
                    modulesFormation={[...formData.modules_formation].sort((a, b) => a.ordre - b.ordre)}
                    getModuleInfo={getModuleInfo}
                    heuresParJour={formData.heures_par_jour}
                    decoupage={formData.decoupage_journees}
                    onChange={(patch) => setFormData(prev => ({ ...prev, ...patch }))}
                  />
                )}
                </div>
                </div>
              </div>
              </TabsContent>
              </Tabs>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Annuler</Button>
                <Button type="submit" className="bg-indigo-600 hover:bg-indigo-700" data-testid="submit-formation">
                  {editingFormation ? 'Mettre à jour' : 'Créer'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Domaine Dialog */}
        <Dialog open={domaineDialogOpen} onOpenChange={setDomaineDialogOpen}>
          <DialogContent className="max-w-sm">
            <DialogHeader>
              <DialogTitle>Nouveau domaine</DialogTitle>
              <DialogDescription>Ajoutez un domaine de formation</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Nom du domaine *</Label>
                <Input value={newDomaine.nom} onChange={(e) => setNewDomaine({ ...newDomaine, nom: e.target.value })} placeholder="Ex: Logistique" data-testid="input-domaine-nom" />
              </div>
              <div className="space-y-2">
                <Label>Abréviation *</Label>
                <Input value={newDomaine.abreviation} onChange={(e) => setNewDomaine({ ...newDomaine, abreviation: e.target.value.toUpperCase() })} placeholder="Ex: LOG" maxLength={6} data-testid="input-domaine-abrev" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDomaineDialogOpen(false)}>Annuler</Button>
              <Button onClick={handleCreateDomaine} disabled={creatingDomaine || !newDomaine.nom || !newDomaine.abreviation} className="bg-indigo-600 hover:bg-indigo-700" data-testid="submit-domaine">
                Créer
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation */}
        <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Supprimer cette formation ?</AlertDialogTitle>
              <AlertDialogDescription>Cette action est irréversible.</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Annuler</AlertDialogCancel>
              <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">Supprimer</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Import Modules Dialog */}
        <Dialog open={importDialogOpen} onOpenChange={setImportDialogOpen}>
          <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Upload className="w-5 h-5 text-indigo-600" />
                Import de modules
              </DialogTitle>
              <DialogDescription>
                {importResult ? `${importResult.total_parsed} module(s) detecte(s) dans le fichier` : 'Analyse en cours...'}
              </DialogDescription>
            </DialogHeader>

            {importResult && (
              <div className="space-y-4">
                {/* Modules found */}
                {importResult.found.length > 0 && (
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <CheckCircle className="w-4 h-4 text-green-500" />
                      <span className="text-sm font-medium text-green-700">{importResult.found.length} module(s) existant(s) en base</span>
                    </div>
                    <div className="border rounded-lg max-h-[200px] overflow-y-auto">
                      <div className="grid grid-cols-[20px_80px_1fr_70px] gap-0 bg-green-50 border-b px-3 py-1 text-[10px] font-medium text-green-700 uppercase tracking-wide sticky top-0">
                        <span></span>
                        <span>Code</span>
                        <span>Titre</span>
                        <span className="text-center">Duree (h)</span>
                      </div>
                      <div className="divide-y">
                        {importResult.found.map((f, i) => (
                          <div key={f.code || f.titre} className="grid grid-cols-[20px_80px_1fr_70px] gap-2 items-center px-3 py-1.5 bg-green-50/30">
                            <CheckCircle className="w-3.5 h-3.5 text-green-500" />
                            <span className="font-mono text-[10px] text-indigo-600">{f.code}</span>
                            <span className="text-xs text-slate-700 truncate">{f.db_module?.titre || f.titre}</span>
                            <Input
                              type="number"
                              value={f.duree_heures}
                              onChange={(e) => {
                                const val = parseInt(e.target.value) || 0;
                                setImportResult(prev => ({
                                  ...prev,
                                  found: prev.found.map((item, idx) => idx === i ? { ...item, duree_heures: val } : item)
                                }));
                              }}
                              className="h-6 text-xs text-center px-1"
                              min="0"
                              data-testid={`found-module-duree-${i}`}
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {/* Modules NOT found */}
                {importResult.not_found.length > 0 && (
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <AlertTriangle className="w-4 h-4 text-amber-500" />
                      <span className="text-sm font-medium text-amber-700">{importResult.not_found.length} module(s) inexistant(s) - creation requise</span>
                    </div>
                    <p className="text-xs text-slate-500 mb-2">Verifiez et modifiez les informations avant de creer les modules.</p>
                    <div className="border border-amber-200 rounded-lg max-h-[300px] overflow-y-auto">
                      <div className="grid grid-cols-[32px_100px_1fr_70px_70px] gap-0 bg-amber-100/50 border-b border-amber-200 px-2 py-1.5 text-[10px] font-medium text-amber-700 uppercase tracking-wide sticky top-0">
                        <span></span>
                        <span>Code</span>
                        <span>Titre</span>
                        <span className="text-center">Duree (h)</span>
                        <span></span>
                      </div>
                      <div className="divide-y divide-amber-100">
                        {modulesToCreate.map((m, i) => (
                          <div key={m._uid} className="grid grid-cols-[32px_100px_1fr_70px_70px] gap-1 items-center px-2 py-1.5 bg-amber-50/30 hover:bg-amber-50" data-testid={`editable-module-${i}`}>
                            <Checkbox
                              checked={m.selected}
                              onCheckedChange={(checked) => {
                                setModulesToCreate(prev => prev.map((mod, idx) => idx === i ? { ...mod, selected: checked } : mod));
                              }}
                            />
                            <Input
                              value={m.code}
                              onChange={(e) => setModulesToCreate(prev => prev.map((mod, idx) => idx === i ? { ...mod, code: e.target.value } : mod))}
                              className="h-7 text-[11px] font-mono px-1.5"
                              data-testid={`edit-module-code-${i}`}
                            />
                            <Input
                              value={m.titre}
                              onChange={(e) => setModulesToCreate(prev => prev.map((mod, idx) => idx === i ? { ...mod, titre: e.target.value } : mod))}
                              className="h-7 text-xs px-1.5"
                              data-testid={`edit-module-titre-${i}`}
                            />
                            <Input
                              type="number"
                              value={m.duree_heures}
                              onChange={(e) => setModulesToCreate(prev => prev.map((mod, idx) => idx === i ? { ...mod, duree_heures: parseInt(e.target.value) || 0 } : mod))}
                              className="h-7 text-xs text-center px-1"
                              min="0"
                              data-testid={`edit-module-duree-${i}`}
                            />
                            <button
                              type="button"
                              onClick={() => setModulesToCreate(prev => prev.filter((_, idx) => idx !== i))}
                              className="p-1 text-red-400 hover:text-red-600 hover:bg-red-50 rounded mx-auto"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                    <Button
                      onClick={handleCreateMissingModules}
                      disabled={creatingModules || modulesToCreate.filter(m => m.selected).length === 0}
                      className="mt-3 bg-amber-600 hover:bg-amber-700 w-full"
                      data-testid="create-missing-modules-btn"
                    >
                      {creatingModules ? (
                        <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Creation en cours...</>
                      ) : (
                        <><Plus className="w-4 h-4 mr-2" />Creer {modulesToCreate.filter(m => m.selected).length} module(s) manquant(s)</>
                      )}
                    </Button>
                  </div>
                )}

                {/* Summary & confirm */}
                {importResult.not_found.length === 0 && importResult.found.length > 0 && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-3 flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-green-500" />
                    <div>
                      <p className="text-sm font-medium text-green-700">Tous les modules existent en base</p>
                      <p className="text-xs text-green-600">Cliquez sur "Confirmer l'import" pour les ajouter a la formation.</p>
                    </div>
                  </div>
                )}
              </div>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={() => setImportDialogOpen(false)}>Annuler</Button>
              <Button
                onClick={confirmImportModules}
                disabled={!importResult || importResult.not_found.length > 0 || importResult.found.length === 0}
                className="bg-indigo-600 hover:bg-indigo-700"
                data-testid="confirm-import-modules-btn"
              >
                <CheckCircle className="w-4 h-4 mr-2" />
                Confirmer l'import ({importResult?.found?.length || 0} modules)
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog open={!!viewingCertification} onOpenChange={() => setViewingCertification(null)}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
            {viewingCertification && (
              <>
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-3">
                    <Award className="w-5 h-5 text-indigo-600" />
                    Fiche de certification
                  </DialogTitle>
                  <DialogDescription>
                    {viewingCertification.code_rncp} — {viewingCertification.titre}
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 mt-2">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-indigo-50 rounded-lg p-3">
                      <p className="text-[10px] uppercase font-semibold text-indigo-500 tracking-wide">
                        {viewingCertification.type_repertoire === 'RS' ? 'Code RS (Répertoire spécifique)' : 'Code RNCP'}
                      </p>
                      <p className="text-lg font-mono font-bold text-indigo-700">{viewingCertification.code_rncp}</p>
                    </div>
                    <div className="bg-slate-50 rounded-lg p-3">
                      <p className="text-[10px] uppercase font-semibold text-slate-500 tracking-wide">Code NSF</p>
                      <p className="text-lg font-mono font-bold text-slate-700">{viewingCertification.code_nsf}</p>
                    </div>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-3">
                    <p className="text-[10px] uppercase font-semibold text-slate-500 tracking-wide">Titre</p>
                    <p className="text-base font-semibold text-slate-800">{viewingCertification.titre}</p>
                  </div>
                  {viewingCertification.organisme_certificateur && (
                    <div className="bg-slate-50 rounded-lg p-3">
                      <p className="text-[10px] uppercase font-semibold text-slate-500 tracking-wide">Organisme certificateur</p>
                      <p className="text-sm text-slate-700">{viewingCertification.organisme_certificateur}</p>
                    </div>
                  )}
                  {viewingCertification.date_echeance && (
                    <div className="bg-amber-50 rounded-lg p-3">
                      <p className="text-[10px] uppercase font-semibold text-amber-500 tracking-wide">Date d'échéance</p>
                      <p className="text-sm font-medium text-amber-700">{viewingCertification.date_echeance}</p>
                    </div>
                  )}
                  {viewingCertification.competences_attestees?.length > 0 && (
                    <div className="space-y-3">
                      <h4 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                        <Shield className="w-4 h-4 text-indigo-500" />
                        Compétences attestées
                      </h4>
                      {viewingCertification.competences_attestees.map((bloc, i) => (
                        <div key={bloc.bloc || i} className="border border-slate-200 rounded-lg p-3">
                          <p className="text-xs font-semibold text-indigo-600 mb-2">{bloc.bloc}</p>
                          <ul className="space-y-1">
                            {bloc.competences.map((comp, j) => (
                              <li key={comp || j} className="text-xs text-slate-600 flex items-start gap-2">
                                <span className="text-indigo-400 mt-0.5">•</span>
                                {comp}
                              </li>
                            ))}
                          </ul>
                        </div>
                      ))}
                    </div>
                  )}
                  {franceCompetencesUrl(viewingCertification) && (
                    <a
                      href={franceCompetencesUrl(viewingCertification)}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="link-france-competences"
                      className="block text-center bg-indigo-600 text-white rounded-lg py-2 text-sm font-medium hover:bg-indigo-700 transition-colors"
                    >
                      Voir la fiche officielle France Compétences →
                    </a>
                  )}
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

// Small reusable components
const InfoBlock = ({ icon: Icon, label, value }) => (
  <div className="flex items-start gap-2.5 p-2.5 bg-slate-50 rounded-lg">
    <Icon className="w-4 h-4 text-slate-500 mt-0.5" />
    <div>
      <p className="text-[10px] font-medium text-slate-400 uppercase">{label}</p>
      <p className="text-sm text-slate-900">{value}</p>
    </div>
  </div>
);

const DetailSection = ({ icon: Icon, title, content }) => (
  <div className="p-3 bg-slate-50 rounded-lg">
    <div className="flex items-center gap-2 mb-2">
      <Icon className="w-4 h-4 text-slate-600" />
      <p className="text-sm font-medium text-slate-700">{title}</p>
    </div>
    <p className="text-slate-600 text-sm whitespace-pre-wrap">{content}</p>
  </div>
);

export default FormationsPage;
